import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './Auth';
import { ensureSegPoolAccount } from './ensureSegPoolAccount';

const waitForCurrentUser = () => new Promise<User | null>((resolve) => {
  if (auth.currentUser) {
    resolve(auth.currentUser);
    return;
  }

  const unsubscribe = onAuthStateChanged(auth, (user) => {
    unsubscribe();
    resolve(user);
  });
});

const toCallbackError = (error: any) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const backendMessage = typeof data === 'string' ? data : data?.message;

  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 500 && backendMessage?.includes('JWT signing key')) return 'jwt_signing_key_not_configured';
  if (status) return 'segpool_verify_failed_' + status;
  return 'segpool_verify_failed';
};

const Verify: React.FC = () => {
  const [accounts, setAccounts] = useState<Array<{ userName: string; isPrimary: boolean }>>([]);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [idToken, setIdToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const redirectToCallback = (params: Record<string, string>) => {
    const url = new URL(window.location.href);
    const returnUri = url.searchParams.get('return_uri');
    if (!returnUri) return;

    const callback = new URL(returnUri);
    Object.entries(params).forEach(([key, value]) => callback.searchParams.set(key, value));
    const state = url.searchParams.get('state');
    if (state) callback.searchParams.set('state', state);
    window.location.replace(callback.toString());
  };

  const mintToken = async (userName: string, token: string) => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const resp = await axios.post('/pool/api/verify', { userName }, {
        headers: {
          token,
          Authorization: `Bearer ${token}`,
        },
      });
      const verificationToken = resp.data?.token;
      if (!verificationToken) throw new Error('No token in response');
      redirectToCallback({ token: verificationToken });
    } catch (error: any) {
      const reason = toCallbackError(error);
      setErrorMessage(error?.response?.data?.message || 'Unable to verify this SegPool account.');
      redirectToCallback({ error: reason });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      let hasAuthenticatedUser = false;
      try {
        const url = new URL(window.location.href);
        const returnUri = url.searchParams.get('return_uri');
        if (!returnUri) {
          console.error('Missing return_uri');
          return;
        }


        const firebaseUser = await waitForCurrentUser();
        const idToken = await firebaseUser?.getIdToken();
        if (!firebaseUser || !idToken) {
          const next = encodeURIComponent(window.location.href);
          window.location.replace(`/connect?next=${next}`);
          return;
        }

        hasAuthenticatedUser = true;

        const availableAccounts = await ensureSegPoolAccount(firebaseUser, idToken);
        if (availableAccounts.length === 0) {
          throw new Error('No SegPool accounts available');
        }

        setIdToken(idToken);
        if (availableAccounts.length === 1) {
          await mintToken(availableAccounts[0].userName, idToken);
          return;
        }

        setAccounts(availableAccounts);
        const primary = availableAccounts.find((account: any) => account.isPrimary);
        setSelectedUserName(primary?.userName || availableAccounts[0].userName);
      } catch (e: any) {
        // If unauthorized, redirect to login with next=current
        if (!hasAuthenticatedUser && e?.response?.status === 401) {
          const next = encodeURIComponent(window.location.href);
          window.location.replace(`/connect?next=${next}`);
          return;
        }
        redirectToCallback({ error: toCallbackError(e) });
      }
    };
    run();
    // The verification request is intentionally initialized once from the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="w-full max-w-md p-6 bg-white dark:bg-slate-800 rounded-lg shadow">
        {accounts.length > 1 ? (
          <div className="space-y-5">
            <div>
              <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Choose SegPool account</h1>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Select the username to link with your Segments account.
              </p>
            </div>
            <select
              value={selectedUserName}
              onChange={(event) => setSelectedUserName(event.target.value)}
              disabled={isSubmitting}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            >
              {accounts.map((account) => (
                <option key={account.userName} value={account.userName}>
                  {account.userName}{account.isPrimary ? ' (Primary)' : ''}
                </option>
              ))}
            </select>
            {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
            <button
              type="button"
              onClick={() => mintToken(selectedUserName, idToken)}
              disabled={isSubmitting || !selectedUserName || !idToken}
              className="w-full rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Verifying...' : 'Continue'}
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 py-4" role="status" aria-live="polite">
            <div className="flex items-center gap-1.5" aria-hidden="true">
              {[0, 150, 300].map((delay) => (
                <span
                  key={delay}
                  className="h-2.5 w-2.5 rounded-full bg-blue-600 animate-bounce"
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
            </div>
            <p className="text-center text-sm font-medium text-slate-600 dark:text-slate-300">
              Verifying your account with SegPool
              <span className="inline-flex w-5 justify-start overflow-hidden align-bottom">
                <span className="animate-pulse">...</span>
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Verify;
